/**
 * CartView — Carts LIST view, extracted from src/pages/CartPage.tsx.
 * Self-contained clone for the Saved Products Cart prototype. List view only.
 */
import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import { Add } from "@faire/slate-icons/Add";
import { ArrowLeft } from "@faire/slate-icons/ArrowLeft";
import { Information } from "@faire/slate-icons/Information";
import { Search } from "@faire/slate-icons/Search";
import { Subtract } from "@faire/slate-icons/Subtract";
import { TrashCan } from "@faire/slate-icons/TrashCan";
import { Truck } from "@faire/slate-icons/Truck";
import { Button } from "@faire/slate/components/button";
import { ProgressBar } from "@faire/slate/components/progress-bar";
import { motion } from "framer-motion";
import React, { useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import RetailerLayout from "../../../components/RetailerLayout";
import { cn } from "../../../lib/utils";

import { CollectionCard } from "./CollectionCard";
import { SaveHeartButton } from "./FavoriteProductTile";
import { SavedCartTile } from "./SavedCartTile";
import {
  ALL_FAVORITES_COLLECTION_ID,
  useCollections,
  useProductTokensByCollection,
} from "./savedStore";
import {
  SAVE_FLOW_POPOVER,
  useFavoritesController,
} from "./useFavoritesController";

const COLLECTIONS_ROUTE = "/cc/saved-products-cart/collections";

export interface CartProduct {
  id: string;
  name: string;
  variant?: string;
  imageUrl: string;
  priceDollars: number;
  msrpDollars?: number;
  qty: number;
  sku?: string;
}

export interface BrandCart {
  id: string;
  brandName: string;
  brandAvatarUrl?: string;
  brandAvatarInitial: string;
  brandAvatarColor: string;
  minimumOrderDollars: number;
  shipsFrom?: string;
  isInsider?: boolean;
  freeShipping?: boolean;
  freeShippingThreshold?: number;
  products: CartProduct[];
}

const MOCK_CARTS: BrandCart[] = [
  {
    id: "noshinku",
    brandName: "Noshinku",
    brandAvatarInitial: "N",
    brandAvatarColor: "#7B9E8B",
    minimumOrderDollars: 100,
    shipsFrom: "Austin, TX",
    isInsider: true,
    freeShipping: true,
    products: [
      {
        id: "n1",
        name: "Eucalyptus Pocket Sanitizer",
        variant: "20mL",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/fd3d710ef1b00e98253d2e76765c2cb111c43daac1870e05e581857ef0c3ccd1.webp",
        priceDollars: 11,
        msrpDollars: 22,
        qty: 7,
        sku: "NOSH-EUC-20",
      },
      {
        id: "n2",
        name: "Bergamot Pocket Sanitizer",
        variant: "20mL",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/fd3d710ef1b00e98253d2e76765c2cb111c43daac1870e05e581857ef0c3ccd1.webp",
        priceDollars: 11,
        msrpDollars: 22,
        qty: 2,
        sku: "NOSH-BRG-20",
      },
    ],
  },
  {
    id: "west-clay",
    brandName: "West Clay Company",
    brandAvatarInitial: "W",
    brandAvatarColor: "#D4A882",
    minimumOrderDollars: 150,
    shipsFrom: "Nashville, TN",
    freeShipping: true,
    freeShippingThreshold: 300,
    products: [
      {
        id: "wc1",
        name: "Concrete Cowgirl Eau de Parfum",
        variant: "1.7 fl oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/9007fa5f19edc20337cc2b2748b73ad3e9fb59ee1eacb1e6e01022ca23bea977.webp",
        priceDollars: 32,
        msrpDollars: 64,
        qty: 2,
        sku: "WCC-CC-170",
      },
      {
        id: "wc2",
        name: "Concrete Cowgirl Body Oil",
        variant: "4 fl oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/9007fa5f19edc20337cc2b2748b73ad3e9fb59ee1eacb1e6e01022ca23bea977.webp",
        priceDollars: 26,
        msrpDollars: 52,
        qty: 3,
        sku: "WCC-BO-40",
      },
      {
        id: "wc3",
        name: "Clean Girl Solid Perfume",
        variant: "0.5 oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/4a6c774f57af8f42fd41a0e4eba5dcf3d1f60cdc2b777557da6148ddfc5021fe.webp",
        priceDollars: 22,
        msrpDollars: 44,
        qty: 2,
        sku: "WCC-CG-05",
      },
      {
        id: "wc4",
        name: "Power Mist Coconut",
        variant: "3 oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/c27a4d1c220729290822c4da0f11afc7ca89a97f830d7010e62e8fe1c040ed47.webp",
        priceDollars: 10,
        msrpDollars: 20,
        qty: 7,
        sku: "WCC-PM-30",
      },
    ],
  },
  {
    id: "be-light",
    brandName: "Be Light Candle Re",
    brandAvatarInitial: "B",
    brandAvatarColor: "#8B7E6B",
    minimumOrderDollars: 100,
    shipsFrom: "Brooklyn, NY",
    isInsider: true,
    freeShipping: true,
    products: [
      {
        id: "bl1",
        name: "You Go Girl Soy Candle",
        variant: "12oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/8559b426957b1d3db4507700897597c7f509890010a3fbb193970f2af742217a.webp",
        priceDollars: 20,
        msrpDollars: 40,
        qty: 2,
        sku: "BLC-YGG-12",
      },
      {
        id: "bl2",
        name: "Shine Bright Soy Candle",
        variant: "12oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/8559b426957b1d3db4507700897597c7f509890010a3fbb193970f2af742217a.webp",
        priceDollars: 20,
        msrpDollars: 40,
        qty: 2,
        sku: "BLC-SB-12",
      },
      {
        id: "bl3",
        name: "Better With Butter Jotter",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/a46ba3f323057317d15a91722a820ca542cd9eeaea646f5f51fc594546340b9f.webp",
        priceDollars: 9,
        msrpDollars: 18,
        qty: 2,
        sku: "BLC-BWB-JT",
      },
    ],
  },
  {
    id: "potluck",
    brandName: "Potluck",
    brandAvatarInitial: "P",
    brandAvatarColor: "#C4956A",
    minimumOrderDollars: 100,
    shipsFrom: "Portland, OR",
    freeShipping: false,
    products: [
      {
        id: "p1",
        name: "Ssamjang Korean Barbeque Sauce",
        variant: "10 oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/c171978c55a1b25e5a677018a94d8113b4387f31e83c6ef062b5a136c29f7986.webp",
        priceDollars: 12,
        qty: 4,
        sku: "PLK-SSJ-10",
      },
      {
        id: "p2",
        name: "Gochujang Red Pepper Paste",
        variant: "8 oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/250be527103d99a45fd96e39a5663c9d442b0a7f4f8bd1b343c6ecde7e011a37.png",
        priceDollars: 10.5,
        qty: 6,
        sku: "PLK-GCH-08",
      },
    ],
  },
  {
    id: "sweet-wink",
    brandName: "Sweet Wink",
    brandAvatarInitial: "S",
    brandAvatarColor: "#E8B4B8",
    minimumOrderDollars: 75,
    shipsFrom: "Dallas, TX",
    isInsider: true,
    freeShipping: true,
    products: [
      {
        id: "sw1",
        name: "Patriotic Cutie Tutu",
        variant: "Girls",
        imageUrl:
          "https://cdn.faire.com/fastly/5e723450294e9288a835f4457d539a0cd62430d121bd23b7e3ea335cd58218b3.jpeg?bg-color=FFFFFF&dpr=1&fit=crop&format=jpg&height=720&precrop=1199,1199,x0,y0,safe&width=720",
        priceDollars: 18,
        msrpDollars: 36,
        qty: 6,
        sku: "SW-PAT-TUTU",
      },
      {
        id: "sw2",
        name: "Mardi Gras Short Sleeve Tutu Bodysuit",
        variant: "Baby",
        imageUrl:
          "https://cdn.faire.com/fastly/cab7385d053843f25aaf0d4228fe8a9e612f3b4b4566606750c4915cd33b4eea.jpeg?bg-color=FFFFFF&dpr=1&fit=crop&format=jpg&height=720&precrop=1199,1198,x0,y0,safe&width=720",
        priceDollars: 16,
        msrpDollars: 32,
        qty: 4,
        sku: "SW-MG-BODY",
      },
    ],
  },
];

export function getCartSubtotal(cart: BrandCart): number {
  return cart.products.reduce((sum, p) => sum + p.priceDollars * p.qty, 0);
}

function getCartItemCount(cart: BrandCart): number {
  return cart.products.reduce((sum, p) => sum + p.qty, 0);
}

export function formatMoney(dollars: number): string {
  return `$${dollars.toFixed(2)}`;
}

// Saved-for-later carts (variant 2). Reuses image URLs already present above.
const MOCK_SAVED_CARTS: BrandCart[] = [
  {
    id: "saved-noshinku",
    brandName: "Noshinku",
    brandAvatarInitial: "N",
    brandAvatarColor: "#7B9E8B",
    minimumOrderDollars: 100,
    shipsFrom: "Austin, TX",
    isInsider: true,
    freeShipping: true,
    products: [
      {
        id: "sv-n1",
        name: "Eucalyptus Pocket Sanitizer",
        variant: "20mL",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/fd3d710ef1b00e98253d2e76765c2cb111c43daac1870e05e581857ef0c3ccd1.webp",
        priceDollars: 11,
        msrpDollars: 22,
        qty: 6,
      },
      {
        id: "sv-n2",
        name: "Clean Girl Solid Perfume",
        variant: "0.5 oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/4a6c774f57af8f42fd41a0e4eba5dcf3d1f60cdc2b777557da6148ddfc5021fe.webp",
        priceDollars: 22,
        msrpDollars: 44,
        qty: 2,
      },
    ],
  },
  {
    id: "saved-be-light",
    brandName: "Be Light Candle Re",
    brandAvatarInitial: "B",
    brandAvatarColor: "#8B7E6B",
    minimumOrderDollars: 100,
    shipsFrom: "Brooklyn, NY",
    freeShipping: true,
    freeShippingThreshold: 200,
    products: [
      {
        id: "sv-bl1",
        name: "You Go Girl Soy Candle",
        variant: "12oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/8559b426957b1d3db4507700897597c7f509890010a3fbb193970f2af742217a.webp",
        priceDollars: 20,
        msrpDollars: 40,
        qty: 2,
      },
      {
        id: "sv-bl2",
        name: "Better With Butter Jotter",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/a46ba3f323057317d15a91722a820ca542cd9eeaea646f5f51fc594546340b9f.webp",
        priceDollars: 9,
        msrpDollars: 18,
        qty: 3,
      },
    ],
  },
  {
    id: "saved-potluck",
    brandName: "Potluck",
    brandAvatarInitial: "P",
    brandAvatarColor: "#C4956A",
    minimumOrderDollars: 100,
    shipsFrom: "Portland, OR",
    freeShipping: false,
    products: [
      {
        id: "sv-p1",
        name: "Ssamjang Korean Barbeque Sauce",
        variant: "10 oz",
        imageUrl:
          "https://cdn.prerelease.faire.dev/fastly/c171978c55a1b25e5a677018a94d8113b4387f31e83c6ef062b5a136c29f7986.webp",
        priceDollars: 12,
        qty: 4,
      },
    ],
  },
];

// ── Brand avatar (matches AvatarWithOverlay) ────────────────────────────────

const BrandAvatar: React.FC<{
  initial: string;
  color: string;
  size?: "xxSmall" | "xSmall";
}> = ({ initial, color, size = "xxSmall" }) => {
  const px = size === "xSmall" ? 40 : 32;
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full"
      style={{ width: px, height: px, backgroundColor: color }}
    >
      <span
        className="text-fs-text-primary-inverse font-medium"
        style={{ fontSize: px * 0.4, lineHeight: 1 }}
      >
        {initial}
      </span>
    </div>
  );
};

// ── Order minimum progress bar ───────────────────────────────────────────────
// Matches BrandMinimumProgressBar from packages/marketplace/incentives/

const OrderMinimumProgressBar: React.FC<{
  current: number;
  minimum: number;
  showMinimumAmount?: boolean;
}> = ({ current, minimum, showMinimumAmount }) => {
  const met = current >= minimum;
  const progress = minimum === 0 ? 1 : Math.min(1, current / minimum);

  const progressText = (() => {
    if (minimum === 0) return "No minimum";
    if (met) {
      return showMinimumAmount
        ? `${formatMoney(minimum)} minimum reached`
        : "Minimum reached";
    }
    return `${formatMoney(minimum)} minimum · ${formatMoney(minimum - current)} to go`;
  })();

  return (
    <div className="w-full">
      <div className="flex flex-row items-center pb-1">
        <span className="type-fs-paragraph text-fs-text-primary">
          {progressText}
        </span>
      </div>
      <ProgressBar progress={progress} showIcon />
    </div>
  );
};

// ── Free shipping badge ─────────────────────────────────────────────────────

const FreeShippingBadge: React.FC<{
  threshold?: number;
}> = ({ threshold }) => (
  <div className="text-fs-message-text-positive flex items-center gap-1">
    <Truck width="14" height="14" />
    <span className="type-fs-paragraph text-fs-message-text-positive">
      {threshold
        ? `Free shipping over ${formatMoney(threshold)}`
        : "Free shipping"}
    </span>
  </div>
);

// ── BrandCartTileImageList ──────────────────────────────────────────────────
// Matches @list/List/BrandCartTile/BrandCartTileImageList.tsx
// Responsive columns: 3 (lg-1280), 4 (1280-1440), 5 (1440+)

const BrandCartTileImageList: React.FC<{
  images: Array<{ url: string; alt: string }>;
  totalProducts: number;
}> = ({ images, totalProducts }) => {
  const renderImages = (count: number) => {
    const remaining = Math.max(0, totalProducts - count);
    return Array.from({ length: count }, (_, i) => {
      const image = images[i];
      const isLast = i === count - 1;
      return (
        <div key={i} className="relative max-h-36 w-36">
          {image ? (
            <img
              src={image.url}
              alt={image.alt}
              className="block h-full w-full object-cover"
              style={{ aspectRatio: "1" }}
            />
          ) : null}
          {isLast && remaining > 0 ? (
            <div className="absolute inset-0 flex items-center justify-center bg-(--slate-color-grey-1000)/40">
              <span className="type-fs-paragraph text-fs-text-primary-inverse font-medium">
                +{remaining}
              </span>
            </div>
          ) : null}
        </div>
      );
    });
  };

  return (
    <>
      {/* 3-column (lg to 1280px) */}
      <div className="hidden min-w-0 flex-row gap-2 min-[1280px]:hidden lg:flex">
        {renderImages(3)}
      </div>
      {/* 4-column (1280px to 1440px) */}
      <div className="hidden min-w-0 flex-row gap-2 min-[1280px]:flex min-[1440px]:hidden">
        {renderImages(4)}
      </div>
      {/* 5-column (1440px+) */}
      <div className="hidden min-w-0 flex-row gap-2 min-[1440px]:flex">
        {renderImages(5)}
      </div>
    </>
  );
};

// ── BrandCartTile (list view) ───────────────────────────────────────────────
// Matches @list/List/BrandCartTile/BrandCartTile.tsx
// Production shows progress bar + buttons on all viewports.

const BrandCartTile: React.FC<{
  cart: BrandCart;
  isChecked: boolean;
  onCheckChange: (id: string, checked: boolean) => void;
  onViewCart: () => void;
}> = ({ cart, isChecked, onCheckChange, onViewCart }) => {
  const subtotal = getCartSubtotal(cart);

  const images = cart.products.map((p) => ({ url: p.imageUrl, alt: p.name }));

  return (
    <div className="relative flex w-full items-center">
      {/* Checkbox — desktop only, absolutely positioned left of card */}
      <div className="relative hidden lg:absolute lg:-left-8 lg:block">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => onCheckChange(cart.id, e.target.checked)}
          className="accent-fs-text-primary h-[18px] w-[18px] cursor-pointer"
        />
      </div>

      {/* Tile card border — matches InteractionWrapper */}
      <div
        className="w-full rounded-lg border lg:rounded"
        style={{ borderColor: "var(--slate-color-action-border-subdued)" }}
      >
        {/* Tile content */}
        <div
          className="hover:bg-fs-surface-secondary flex w-full min-w-0 cursor-pointer justify-between gap-3 p-4 transition-all duration-300 ease-in-out lg:gap-x-8 lg:p-8"
          onClick={onViewCart}
        >
          {/* Left column: brand info + progress + buttons */}
          <div className="flex w-full min-w-[204px] flex-1 flex-col gap-3 lg:max-w-96 lg:gap-4">
            {/* Brand header row */}
            <div className="flex items-center justify-between gap-1">
              {/* Mobile checkbox */}
              <div className="block shrink-0 lg:hidden">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => onCheckChange(cart.id, e.target.checked)}
                  className="accent-fs-text-primary h-[18px] w-[18px] cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              <div className="flex min-w-0 flex-1 cursor-pointer flex-row items-center gap-2">
                <BrandAvatar
                  initial={cart.brandAvatarInitial}
                  color={cart.brandAvatarColor}
                  size="xxSmall"
                />
                <div className="min-w-0">
                  <h5 className="type-fs-paragraph-medium truncate">
                    {cart.brandName}
                  </h5>
                  {cart.freeShipping ? (
                    <FreeShippingBadge threshold={cart.freeShippingThreshold} />
                  ) : null}
                </div>
              </div>

              <div className="shrink-0">
                <p className="type-fs-paragraph">{formatMoney(subtotal)}</p>
              </div>
            </div>

            {/* Mobile image list */}
            <div className="lg:hidden">
              <div className="grid grid-cols-5 items-center gap-px">
                {cart.products.slice(0, 5).map((product, i) => {
                  const remaining = Math.max(0, cart.products.length - 5);
                  const isLast = i === 4;
                  return (
                    <div
                      key={product.id}
                      className="relative max-h-36 max-w-36 overflow-hidden"
                    >
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="block w-full object-cover"
                        style={{ aspectRatio: "1" }}
                      />
                      {isLast && remaining > 0 ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-(--slate-color-grey-1000)/40">
                          <span className="type-fs-label text-fs-text-primary-inverse font-medium">
                            +{remaining}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Progress bar + buttons — always visible */}
            <div className="mt-0 w-full">
              <OrderMinimumProgressBar
                current={subtotal}
                minimum={cart.minimumOrderDollars}
              />
            </div>
            <div
              className="flex w-full gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex-1">
                <Button variant="secondary" size="small" fullWidth>
                  Shop brand
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  variant="primary"
                  size="small"
                  fullWidth
                  onClick={onViewCart}
                >
                  View cart
                </Button>
              </div>
            </div>
          </div>

          {/* Right column: desktop product images */}
          <div className="hidden min-w-0 items-center lg:flex">
            <BrandCartTileImageList
              images={images}
              totalProducts={cart.products.length}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Cart header ─────────────────────────────────────────────────────────────
// Matches @header/Header/Header.tsx

const CartHeader: React.FC<{
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onOpenCollections: () => void;
}> = ({ searchQuery, onSearchChange, onOpenCollections }) => (
  <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-20 xl:px-0">
    <div className="pt-4">
      <h1
        className="type-fs-display-s text-fs-text-primary"
        style={{
          fontFamily: "var(--slate-typography-family-serif), Georgia, serif",
        }}
      >
        Carts
      </h1>
    </div>
    <div className="flex items-center justify-between py-4">
      <div
        className="flex h-10 w-[417px] items-center rounded-full border px-4"
        style={{
          gap: "var(--slate-spacing-element-default)",
          borderColor: "var(--slate-color-action-border-subdued)",
          backgroundColor: "var(--slate-color-action-surface-inverse)",
        }}
      >
        <span className="text-fs-text-subdued">
          <Search width="16" height="16" />
        </span>
        <input
          type="text"
          placeholder="Search for products or brands"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="type-fs-paragraph text-fs-text-primary min-w-0 flex-1 border-none bg-transparent outline-none"
        />
      </div>
      <Button variant="secondary" size="small" onClick={onOpenCollections}>
        Collections
      </Button>
    </div>
  </div>
);

// ── Carts & Saved header (variant 2) ────────────────────────────────────────
// LEFT: serif page title that animates between "Carts" and "Saved".
// CENTER: segmented FilterToggle switching the active tab.
// RIGHT: the search field (reuses CartHeader's search styling).

const CartsSavedHeader: React.FC<{
  savedTab: "all" | "saved";
  onTabChange: (tab: "all" | "saved") => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
}> = ({ savedTab, onTabChange, searchQuery, onSearchChange }) => (
  <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-20 xl:px-0">
    <div className="flex items-center justify-between gap-4 py-4">
      {/* LEFT — animated serif title */}
      <div className="min-w-[140px]">
        <motion.h1
          key={savedTab}
          className="type-fs-display-s text-fs-text-primary"
          style={{
            fontFamily: "var(--slate-typography-family-serif), Georgia, serif",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          {savedTab === "all" ? "Carts" : "Saved"}
        </motion.h1>
      </div>

      {/* CENTER — segmented switch */}
      <div
        className="flex h-10 items-center gap-1 rounded-full border p-0.5"
        style={{ borderColor: "var(--slate-color-action-border-subdued)" }}
      >
        <SavedTabSwitchButton
          label="All carts"
          active={savedTab === "all"}
          onClick={() => onTabChange("all")}
        />
        <SavedTabSwitchButton
          label="Saved"
          active={savedTab === "saved"}
          onClick={() => onTabChange("saved")}
        />
      </div>

      {/* RIGHT — search field */}
      <div
        className="flex h-10 w-[300px] items-center rounded-full border px-4"
        style={{
          gap: "var(--slate-spacing-element-default)",
          borderColor: "var(--slate-color-action-border-subdued)",
          backgroundColor: "var(--slate-color-action-surface-inverse)",
        }}
      >
        <span className="text-fs-text-subdued">
          <Search width="16" height="16" />
        </span>
        <input
          type="text"
          placeholder="Search carts"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="type-fs-paragraph text-fs-text-primary min-w-0 flex-1 border-none bg-transparent outline-none"
        />
      </div>
    </div>
  </div>
);

const SavedTabSwitchButton: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "type-fs-paragraph flex h-full cursor-pointer items-center rounded-full border-none px-4 whitespace-nowrap transition-colors",
      active
        ? "text-fs-text-primary-inverse bg-[#333]"
        : "text-fs-text-subdued bg-transparent"
    )}
  >
    {label}
  </button>
);

// ── Summary footer (list view) ──────────────────────────────────────────────
// Matches @summaryFooter/SummaryFooter/SummaryFooter.tsx
// Fixed bottom bar with total + checkout button.

const SummaryFooter: React.FC<{
  carts: BrandCart[];
}> = ({ carts }) => {
  const totalBrands = carts.length;
  const totalItems = carts.reduce((sum, c) => sum + getCartItemCount(c), 0);
  const totalDollars = carts.reduce((sum, c) => sum + getCartSubtotal(c), 0);

  return (
    <div className="border-fs-border-muted bg-fs-surface-primary fixed bottom-0 z-10 w-full border-t shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.12)]">
      <div className="mx-auto flex items-center justify-between px-4 py-4 md:max-w-[1280px] xl:px-0">
        {/* Summary info */}
        <div className="flex flex-col">
          <h5 className="type-fs-paragraph-medium">
            {totalBrands} brand {totalBrands === 1 ? "cart" : "carts"}
          </h5>
          <h5 className="type-fs-paragraph">
            Item total: {formatMoney(totalDollars)}
          </h5>
        </div>

        {/* Actions + Checkout */}
        <div className="flex items-center gap-4">
          <div className="ml-2 min-w-[132px] md:ml-6 md:min-w-[175px]">
            <Button variant="primary" size="small" fullWidth>
              Checkout all
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Quantity picker ──────────────────────────────────────────────────────────

const QuantityPicker: React.FC<{
  value: number;
  onChange: (v: number) => void;
}> = ({ value, onChange }) => (
  <div className="border-fs-border-muted flex h-9 items-center rounded border">
    <button
      className="text-fs-text-primary hover:bg-fs-surface-secondary flex h-full w-9 items-center justify-center border-none bg-transparent transition-colors"
      style={{
        cursor: "pointer",
        borderRight: "1px solid var(--slate-color-border-muted)",
      }}
      onClick={() => onChange(Math.max(1, value - 1))}
    >
      <Subtract width="12" height="12" />
    </button>
    <span className="type-fs-paragraph text-fs-text-primary flex h-full w-10 items-center justify-center">
      {value}
    </span>
    <button
      className="text-fs-text-primary hover:bg-fs-surface-secondary flex h-full w-9 items-center justify-center border-none bg-transparent transition-colors"
      style={{
        cursor: "pointer",
        borderLeft: "1px solid var(--slate-color-border-muted)",
      }}
      onClick={() => onChange(value + 1)}
    >
      <Add width="12" height="12" />
    </button>
  </div>
);

// ── Detail header ───────────────────────────────────────────────────────────
// Matches [brandToken]/Details/DetailsHeader.tsx

const DetailsHeader: React.FC<{
  cart: BrandCart;
}> = ({ cart }) => {
  const subtotal = getCartSubtotal(cart);
  const itemCount = getCartItemCount(cart);

  return (
    <div className="bg-fs-surface-primary flex min-h-[79px] shrink-0 flex-col py-4 lg:flex-row lg:justify-between lg:gap-4">
      <div className="flex flex-row items-center gap-x-2 gap-y-4">
        <div className="flex min-w-0 flex-1 flex-row items-center gap-1">
          <BrandAvatar
            initial={cart.brandAvatarInitial}
            color={cart.brandAvatarColor}
            size="xSmall"
          />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
              <h5 className="type-fs-subheading-medium truncate">
                {cart.brandName}
              </h5>
            </div>
            {cart.isInsider ? (
              <span className="type-fs-paragraph text-fs-text-subdued">
                Insider
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Save for later — desktop */}
      <div className="hidden lg:block lg:self-center">
        <Button variant="secondary" size="small">
          Save for later
        </Button>
      </div>

      {/* Mobile progress bar */}
      <div className="mt-4 w-full min-w-0 flex-1 lg:hidden">
        <OrderMinimumProgressBar
          current={subtotal}
          minimum={cart.minimumOrderDollars}
          showMinimumAmount
        />
      </div>

      {/* Cart summary (items + total) */}
      <div className="flex items-center gap-4 pt-2 lg:hidden">
        <span className="type-fs-paragraph text-fs-text-subdued">
          {itemCount} {itemCount === 1 ? "item" : "items"}
        </span>
        <span className="type-fs-paragraph-medium">
          {formatMoney(subtotal)}
        </span>
      </div>
    </div>
  );
};

// ── Cart item row (detail panel) ─────────────────────────────────────────────
// Matches [brandToken]/Details/BrandCartItem/ components

const CartItemRow: React.FC<{
  product: CartProduct;
  onQtyChange: (id: string, qty: number) => void;
  onRemove: (id: string) => void;
  isFavorited: boolean;
  onFavorite: (
    productId: string,
    isFavorited: boolean,
    anchorElement: HTMLElement,
    productImageUrl?: string
  ) => void;
  isLast?: boolean;
}> = ({ product, onQtyChange, onRemove, isFavorited, onFavorite, isLast }) => (
  <div className={cn("py-4", isLast ? "" : "border-fs-border-muted border-b")}>
    <div className="flex gap-4">
      <div className="w-[70px] shrink-0 md:w-[132px]">
        <div
          className="relative aspect-square overflow-hidden rounded"
          onMouseDownCapture={(event) => {
            const target = event.target;
            if (
              target instanceof Element &&
              target.closest("[data-favorite-button]")
            ) {
              event.stopPropagation();
            }
          }}
        >
          <img
            src={product.imageUrl}
            alt={product.name}
            className="block h-full w-full object-cover"
          />
          <SaveHeartButton
            isFavorited={isFavorited}
            onFavorite={(anchorElement) =>
              onFavorite(
                product.id,
                isFavorited,
                anchorElement,
                product.imageUrl
              )
            }
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2 md:gap-4">
        <div>
          <p className="type-fs-paragraph-medium text-fs-text-primary">
            {product.name}
          </p>
          {product.variant ? (
            <p className="type-fs-label text-fs-text-subdued">
              {product.variant}
            </p>
          ) : null}
        </div>

        <div>
          <span className="type-fs-paragraph-medium text-fs-text-primary">
            {formatMoney(product.priceDollars)}
          </span>
          {product.msrpDollars ? (
            <span className="type-fs-label text-fs-text-subdued ml-2 line-through">
              {formatMoney(product.msrpDollars)}
            </span>
          ) : null}
        </div>

        <div className="flex shrink-0 items-end justify-between">
          <QuantityPicker
            value={product.qty}
            onChange={(v) => onQtyChange(product.id, v)}
          />
          <button
            className="text-fs-text-subdued hover:text-fs-text-primary flex items-center justify-center border-none bg-transparent p-1 transition-colors"
            style={{ cursor: "pointer" }}
            onClick={() => onRemove(product.id)}
          >
            <TrashCan width="18" height="18" />
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ── Order summary (detail view) ─────────────────────────────────────────────
// Matches [brandToken]/Details/BrandCartOrderSummary/BrandCartOrderSummary.tsx

const BrandCartOrderSummary: React.FC<{
  cart: BrandCart;
}> = ({ cart }) => {
  const subtotal = getCartSubtotal(cart);
  const met = subtotal >= cart.minimumOrderDollars;

  return (
    <div className="flex flex-col gap-4">
      <div className="border-fs-border-muted flex flex-col gap-4 p-4 lg:border lg:p-6">
        <h3 className="text-fs-text-primary lg:type-fs-subheading-medium">
          Order summary
        </h3>

        {/* Subtotal line */}
        <div className="flex items-center justify-between">
          <span className="type-fs-paragraph">Subtotal</span>
          <span className="type-fs-paragraph">{formatMoney(subtotal)}</span>
        </div>

        {/* Estimated total */}
        <div className="border-fs-border-muted flex items-center justify-between border-t pt-4">
          <span className="type-fs-paragraph-medium">Estimated total</span>
          <span className="type-fs-paragraph-medium">
            {formatMoney(subtotal)}
          </span>
        </div>

        {/* Insider promo banner */}
        <div className="border-fs-border-muted bg-fs-surface-primary flex items-start gap-2.5 rounded-md border p-3">
          <span className="text-fs-message-text-positive mt-0.5 shrink-0">
            <Information width="16" height="16" />
          </span>
          <p className="type-fs-label text-fs-text-primary">
            Unlock <span className="underline">free shipping</span> on thousands
            of brands with an Insider membership. Free for 30 days.{" "}
            <span className="cursor-pointer underline">Learn more</span>
          </p>
        </div>

        {/* CTA button */}
        <div>
          <Button variant="primary" fullWidth disabled={!met}>
            Proceed to checkout
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Sidebar brand tile (detail view) ────────────────────────────────────────

const SidebarBrandTile: React.FC<{
  cart: BrandCart;
  isSelected: boolean;
  onClick: () => void;
}> = ({ cart, isSelected, onClick }) => {
  const subtotal = getCartSubtotal(cart);

  return (
    <button
      onClick={onClick}
      className="bg-fs-surface-primary block w-full cursor-pointer rounded-lg border p-3 text-left transition-all duration-200"
      style={{
        borderWidth: isSelected ? 2 : 1,
        borderColor: isSelected
          ? "var(--slate-color-text-primary)"
          : "var(--slate-color-border-muted)",
      }}
    >
      <div className="mb-2 flex items-center gap-2">
        <BrandAvatar
          initial={cart.brandAvatarInitial}
          color={cart.brandAvatarColor}
          size="xxSmall"
        />
        <div className="min-w-0 flex-1">
          <span className="type-fs-label-medium text-fs-text-primary block truncate">
            {cart.brandName}
          </span>
        </div>
        <span className="type-fs-label text-fs-text-primary shrink-0">
          {formatMoney(subtotal)}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {cart.products.slice(0, 4).map((product) => (
          <div
            key={product.id}
            className="bg-fs-surface-secondary h-14 w-14 overflow-hidden rounded"
          >
            <img
              src={product.imageUrl}
              alt={product.name}
              className="block h-full w-full object-cover"
            />
          </div>
        ))}
        {cart.products.length > 4 ? (
          <div className="bg-fs-surface-secondary flex h-14 w-14 items-center justify-center rounded">
            <span className="type-fs-label text-fs-text-subdued">
              +{cart.products.length - 4}
            </span>
          </div>
        ) : null}
      </div>
    </button>
  );
};

// ── CartLayout ──────────────────────────────────────────────────────────────
// Matches cart/CartLayout.tsx — two-panel layout with gradient curtain transition.

const CartLayout: React.FC<{
  isDetailsVisible: boolean;
  header: React.ReactNode;
  list: React.ReactNode;
  children: React.ReactNode;
  summaryFooter: React.ReactNode;
}> = ({ isDetailsVisible, header, list, children, summaryFooter }) => {
  return (
    <div
      className={cn(
        "bg-fs-surface-primary flex w-full flex-1 flex-col",
        isDetailsVisible && "overflow-hidden"
      )}
      style={isDetailsVisible ? { height: "calc(100vh - 64px)" } : undefined}
    >
      {!isDetailsVisible ? header : null}
      <div
        className={cn(
          "mx-auto flex min-h-0 w-full flex-1 flex-col px-4 lg:flex-row",
          isDetailsVisible ? "pr-0" : "lg:max-w-[1280px] lg:px-20 xl:px-0"
        )}
        style={{ transition: "none" }}
      >
        {/* List panel */}
        <div
          className={cn(
            "cart-list-panel w-full",
            isDetailsVisible &&
              "no-scrollbar border-fs-border-muted overflow-y-auto lg:w-[334px] lg:border-r"
          )}
          style={{ transition: "none" }}
        >
          {isDetailsVisible ? header : null}
          {list}
        </div>

        {/* Details panel */}
        <div
          className={cn(
            "cart-details-panel relative overflow-x-hidden lg:min-h-0 lg:flex-1",
            isDetailsVisible
              ? "pointer-events-auto translate-x-0 overflow-y-auto opacity-100"
              : "pointer-events-none translate-x-0 opacity-20 lg:w-0 lg:overflow-hidden"
          )}
          style={{ transition: "all 300ms ease-in-out" }}
        >
          {/* Gradient curtain — slides away when details visible */}
          <div
            aria-hidden
            className={cn(
              "via-fs-surface-primary/60 to-fs-surface-primary pointer-events-none absolute inset-0 z-10 bg-gradient-to-l from-transparent from-[0%] via-[8%] to-[16%] transition-transform duration-300 ease-in-out will-change-transform",
              isDetailsVisible ? "-translate-x-full" : "translate-x-0"
            )}
          />
          {children}
        </div>
      </div>
      {!isDetailsVisible ? summaryFooter : null}
    </div>
  );
};

// ── CartView ─────────────────────────────────────────────────────────────────

interface CartViewProps {
  savedSection?: React.ReactNode;
  cartHref: string;
  onOpenCollections: () => void;
  /** Account dropdown for the global nav (see `RetailerLayout`). */
  accountDropdown?: (close: () => void) => React.ReactNode;
}

export const CartView = ({
  savedSection,
  cartHref,
  onOpenCollections,
  accountDropdown,
}: CartViewProps) => {
  const [carts, setCarts] = useState<BrandCart[]>(MOCK_CARTS);
  const [selectedCartId, setSelectedCartId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [checkedCartIds, setCheckedCartIds] = useState<Set<string>>(new Set());
  const [variant, setVariant] = useState(1);
  const [savedTab, setSavedTab] = useState<"all" | "saved">("all");
  const favorites = useFavoritesController({
    saveFlowVariant: SAVE_FLOW_POPOVER,
  });
  const history = useHistory();
  const productTokensByCollection = useProductTokensByCollection();
  const collections = useCollections();

  const handleCheckChange = useCallback((cartId: string, checked: boolean) => {
    setCheckedCartIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(cartId);
      } else {
        next.delete(cartId);
      }
      return next;
    });
  }, []);

  const selectedCart = useMemo(
    () =>
      carts.find((c) => c.id === selectedCartId) ??
      MOCK_SAVED_CARTS.find((c) => c.id === selectedCartId) ??
      null,
    [carts, selectedCartId]
  );

  const isDetailView = selectedCart !== null;

  const filteredCarts = useMemo(() => {
    if (!searchQuery.trim()) return carts;
    const q = searchQuery.toLowerCase();
    return carts.filter(
      (c) =>
        c.brandName.toLowerCase().includes(q) ||
        c.products.some((p) => p.name.toLowerCase().includes(q))
    );
  }, [carts, searchQuery]);

  const handleQtyChange = useCallback(
    (productId: string, qty: number) => {
      setCarts((prev) =>
        prev.map((cart) =>
          cart.id === selectedCartId
            ? {
                ...cart,
                products: cart.products.map((p) =>
                  p.id === productId ? { ...p, qty } : p
                ),
              }
            : cart
        )
      );
    },
    [selectedCartId]
  );

  const handleRemove = useCallback(
    (productId: string) => {
      setCarts((prev) =>
        prev.map((cart) =>
          cart.id === selectedCartId
            ? {
                ...cart,
                products: cart.products.filter((p) => p.id !== productId),
              }
            : cart
        )
      );
    },
    [selectedCartId]
  );

  const isSavedAndSavedTab = variant === 2 && savedTab === "saved";

  // ── Header (list view header, or detail sidebar back control) ──────────
  const listViewHeader =
    variant === 2 ? (
      <CartsSavedHeader
        savedTab={savedTab}
        onTabChange={setSavedTab}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />
    ) : (
      <CartHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onOpenCollections={onOpenCollections}
      />
    );

  const headerNode = isDetailView ? (
    <div className="bg-fs-surface-primary sticky inset-0 z-10 px-6 pt-4 pb-[14px]">
      <div className="flex items-center">
        <button
          className="text-fs-text-primary hover:bg-fs-surface-secondary flex h-10 w-10 items-center justify-center rounded-full border-none bg-transparent transition-colors"
          style={{ cursor: "pointer" }}
          onClick={() => setSelectedCartId(null)}
        >
          <ArrowLeft width="20" height="20" />
        </button>
        <h5 className="type-fs-subheading-medium">Carts</h5>
      </div>
    </div>
  ) : (
    listViewHeader
  );

  // ── Brand-cart list (shared by variant 1 and variant 2 "All carts") ────
  const brandCartList = (
    <div className="flex w-full flex-col space-y-3 pb-3 lg:pb-24">
      {filteredCarts.map((cart) => (
        <BrandCartTile
          key={cart.id}
          cart={cart}
          isChecked={checkedCartIds.has(cart.id)}
          onCheckChange={handleCheckChange}
          onViewCart={() => setSelectedCartId(cart.id)}
        />
      ))}
    </div>
  );

  // ── Saved tab content (variant 2) ──────────────────────────────────────
  const savedTabContent = (
    <div className="flex w-full flex-col gap-8 pb-3 lg:pb-24">
      <div className="flex gap-6 overflow-x-auto pb-1">
        {collections.map((collection) => {
          const isAllSaved = collection.id === ALL_FAVORITES_COLLECTION_ID;
          return (
            <CollectionCard
              key={collection.id}
              collection={collection}
              productTokensByCollection={productTokensByCollection}
              label={isAllSaved ? "All saved products" : undefined}
              onClick={() =>
                history.push(
                  isAllSaved
                    ? COLLECTIONS_ROUTE
                    : `${COLLECTIONS_ROUTE}/${collection.id}`
                )
              }
            />
          );
        })}
      </div>
      <section className="flex flex-col gap-4">
        <h2 className="type-fs-subheading-medium text-fs-text-primary">
          Saved carts
        </h2>
        <div className="flex flex-col gap-4">
          {MOCK_SAVED_CARTS.map((cart) => (
            <SavedCartTile
              key={cart.id}
              cart={cart}
              onViewCart={() => setSelectedCartId(cart.id)}
            />
          ))}
        </div>
      </section>
    </div>
  );

  // ── List (brand tiles) ────────────────────────────────────────────────
  const listNode = isDetailView ? (
    <div className="flex flex-col space-y-3 px-6 pb-3">
      {carts.map((cart) => (
        <SidebarBrandTile
          key={cart.id}
          cart={cart}
          isSelected={cart.id === selectedCartId}
          onClick={() => setSelectedCartId(cart.id)}
        />
      ))}
    </div>
  ) : isSavedAndSavedTab ? (
    savedTabContent
  ) : (
    <>
      {variant === 1 ? savedSection : null}
      {brandCartList}
    </>
  );

  // ── Detail content ────────────────────────────────────────────────────
  const detailContent = selectedCart ? (
    <div className="flex flex-col pl-0">
      {/* Sticky detail header */}
      <div className="border-fs-border-muted top-0 z-10 mx-4 border-b lg:sticky lg:mx-0">
        <div className="bg-fs-surface-primary mx-auto w-full max-w-[1560px] lg:sticky lg:top-0 lg:pr-10 lg:pl-6">
          <DetailsHeader cart={selectedCart} />
        </div>
      </div>

      {/* Main content area */}
      <div className="mx-auto w-full max-w-[1560px] lg:pr-10 lg:pb-4 lg:pl-6">
        <div className="flex flex-col gap-y-4 lg:flex-row lg:gap-6">
          {/* Items column */}
          <div className="order-1 flex-1">
            {/* Desktop progress bar */}
            <div className="border-fs-border-muted mt-4 hidden w-full min-w-0 border-b pb-4 lg:block">
              <OrderMinimumProgressBar
                current={getCartSubtotal(selectedCart)}
                minimum={selectedCart.minimumOrderDollars}
                showMinimumAmount
              />
            </div>

            <ul className="flex flex-col">
              {selectedCart.products.map((product, i) => (
                <CartItemRow
                  key={product.id}
                  product={product}
                  onQtyChange={handleQtyChange}
                  onRemove={handleRemove}
                  isFavorited={favorites.isSaved(product.id)}
                  onFavorite={favorites.handleFavoriteClick}
                  isLast={i === selectedCart.products.length - 1}
                />
              ))}
            </ul>
          </div>

          {/* Order summary column — sticky */}
          <div className="order-2 lg:w-[305px]">
            <div className="gap-4 lg:sticky lg:top-24">
              <BrandCartOrderSummary cart={selectedCart} />
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <LiveToolbar
        variantGroups={[
          {
            title: "Cart layout",
            queryKey: "cart-variant",
            variants: [
              { variantNumber: 1, label: "Standard" },
              { variantNumber: 2, label: "Carts & Saved" },
            ],
            active: variant,
            onChange: setVariant,
          },
        ]}
      />
      <RetailerLayout
        stickyNav
        cartHref={cartHref}
        logoLinkUrl="/cc/saved-products-cart"
        hideFooter
        accountDropdown={accountDropdown}
      >
        <CartLayout
          isDetailsVisible={isDetailView}
          header={headerNode}
          list={listNode}
          summaryFooter={
            checkedCartIds.size > 0 ? (
              <SummaryFooter
                carts={carts.filter((c) => checkedCartIds.has(c.id))}
              />
            ) : null
          }
        >
          {detailContent}
        </CartLayout>
      </RetailerLayout>
      {favorites.popoverElement}
      {favorites.toastElement}
    </>
  );
};
